"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
var _classCallCheck2 = _interopRequireDefault(require("@babel/runtime/helpers/classCallCheck"));
var _createClass2 = _interopRequireDefault(require("@babel/runtime/helpers/createClass"));
/* eslint-disable object-curly-newline, no-underscore-dangle */
var EventDispatcher = require('../utils/event-dispatcher');
var _require = require('../utils/helpers'),
  convertResponseToJson = _require.convertResponseToJson,
  cleanParams = _require.cleanParams,
  applyNetworkTimeout = _require.applyNetworkTimeout,
  trimNonBreakingSpaces = _require.trimNonBreakingSpaces,
  encodeURIComponentRFC3986 = _require.encodeURIComponentRFC3986,
  stringify = _require.stringify;

// Create URL from supplied query (term) and parameters
function createAutocompleteUrl(query, parameters, options) {
  var apiKey = options.apiKey,
    version = options.version,
    serviceUrl = options.serviceUrl,
    sessionId = options.sessionId,
    clientId = options.clientId,
    userId = options.userId,
    segments = options.segments,
    testCells = options.testCells;
  var queryParams = {
    c: version
  };
  queryParams.key = apiKey;
  queryParams.i = clientId;
  queryParams.s = sessionId;

  // Validate query (term) is provided
  if (!query || typeof query !== 'string') {
    throw new Error('query is a required parameter of type string');
  }

  // Pull test cells from options
  if (testCells) {
    Object.keys(testCells).forEach(function (testCellKey) {
      queryParams["ef-".concat(testCellKey)] = testCells[testCellKey];
    });
  }

  // Pull user segments from options
  if (segments && segments.length) {
    queryParams.us = segments;
  }

  // Pull user id from options and ensure string
  if (userId) {
    queryParams.ui = String(userId);
  }
  if (parameters) {
    var numResults = parameters.numResults,
      resultsPerSection = parameters.resultsPerSection,
      filters = parameters.filters,
      filtersPerSection = parameters.filtersPerSection,
      hiddenFields = parameters.hiddenFields,
      variationsMap = parameters.variationsMap,
      preFilterExpression = parameters.preFilterExpression,
      qsParam = parameters.qsParam,
      fmtOptions = parameters.fmtOptions;

    // Pull results number from parameters
    if (numResults) {
      queryParams.num_results = numResults;
    }

    // Pull results number per section from parameters
    if (resultsPerSection) {
      Object.keys(resultsPerSection).forEach(function (section) {
        queryParams["num_results_".concat(section)] = resultsPerSection[section];
      });
    }

    // Pull filters from parameters
    if (filters) {
      queryParams.filters = filters;
    }

    // Pull filtersPerSection from parameters
    if (filtersPerSection) {
      Object.keys(filtersPerSection).forEach(function (section) {
        queryParams["filters[".concat(section, "]")] = filtersPerSection[section];
      });
    }

    // Pull filter expression from parameters
    if (preFilterExpression) {
      queryParams.pre_filter_expression = JSON.stringify(preFilterExpression);
    }

    // Pull format options from parameters
    if (fmtOptions) {
      queryParams.fmt_options = fmtOptions;
    }

    // Pull hidden fields from parameters
    if (hiddenFields) {
      if (queryParams.fmt_options) {
        queryParams.fmt_options.hidden_fields = hiddenFields;
      } else {
        queryParams.fmt_options = {
          hidden_fields: hiddenFields
        };
      }
    }

    // Pull variations map from parameters
    if (variationsMap) {
      queryParams.variations_map = JSON.stringify(variationsMap);
    }

    // pull qs param from parameters
    if (qsParam) {
      queryParams.qs = JSON.stringify(qsParam);
    }
  }
  queryParams._dt = Date.now();
  queryParams = cleanParams(queryParams);
  var queryString = stringify(queryParams);
  var cleanedQuery = query.replace(/^\//, '|'); // For compatibility with backend API

  return "".concat(serviceUrl, "/autocomplete/").concat(encodeURIComponentRFC3986(trimNonBreakingSpaces(cleanedQuery)), "?").concat(queryString);
}

/**
 * Interface to autocomplete related API calls.
 *
 * @module autocomplete
 * @inner
 * @returns {object}
 */
var Autocomplete = /*#__PURE__*/function () {
  function Autocomplete(options) {
    (0, _classCallCheck2["default"])(this, Autocomplete);
    this.options = options || {};
    this.eventDispatcher = new EventDispatcher(options.eventDispatcher);
  }

  /**
   * Retrieve autocomplete results from API
   *
   * @function getAutocompleteResults
   * @description Retrieve autocomplete results from Constructor.io API
   * @param {string} query - Term to use to perform an autocomplete search
   * @param {object} [parameters] - Additional parameters to refine result set
   * @param {number} [parameters.numResults] - The total number of results to return
   * @param {object} [parameters.filters] - Key / value mapping (dictionary) of filters used to refine results
   * @param {object} [parameters.filtersPerSection] - Filters used to refine results per section
   * @param {object} [parameters.resultsPerSection] - Number of results to return (value) per section (key)
   * @param {object} [parameters.fmtOptions] - An object containing options to format different aspects of the response. Please refer to https://docs.constructor.com/reference/v1-autocomplete-get-autocomplete-results for details
   * @param {object} [parameters.preFilterExpression] - Faceting expression to scope autocomplete results. Please refer to https://docs.constructor.com/reference/configuration-collections for details
   * @param {string[]} [parameters.hiddenFields] - Hidden metadata fields to return
   * @param {object} [parameters.variationsMap] - The variations map object to aggregate variations. Please refer to https://docs.constructor.com/reference/shared-variations-mapping for details
   * @param {object} [parameters.qsParam] - object of additional query parameters to be appended to requests for results
   * @param {object} [networkParameters] - Parameters relevant to the network request
   * @param {number} [networkParameters.timeout] - Request timeout (in milliseconds)
   * @returns {Promise}
   * @see https://docs.constructor.com/reference/autocomplete-autocomplete-results
   * @example
   * constructorio.autocomplete.getAutocompleteResults('t-shirt', {
   *     resultsPerSection: {
   *         Products: 5,
   *         'Search Suggestions': 10,
   *     },
   *     filters: {
   *         size: 'medium'
   *     },
   * });
   */
  (0, _createClass2["default"])(Autocomplete, [{
    key: "getAutocompleteResults",
    value: function getAutocompleteResults(query, parameters) {
      var _this = this;
      var networkParameters = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
      var requestUrl;
      var fetch = this.options.fetch;
      var signal;
      if (typeof AbortController === 'function') {
        var controller = new AbortController();
        signal = controller && controller.signal;

        // Handle network timeout if specified
        applyNetworkTimeout(this.options, networkParameters, controller);
      }
      try {
        requestUrl = createAutocompleteUrl(query, parameters, this.options);
      } catch (e) {
        return Promise.reject(e);
      }
      return fetch(requestUrl, {
        signal: signal
      }).then(convertResponseToJson).then(function (json) {
        if (json.sections) {
          if (json.result_id) {
            var sectionKeys = Object.keys(json.sections);
            sectionKeys.forEach(function (section) {
              var sectionItems = json.sections[section];
              if (sectionItems.length) {
                // Append `result_id` to each section item
                sectionItems.forEach(function (item) {
                  // eslint-disable-next-line no-param-reassign
                  item.result_id = json.result_id;
                });
              }
            });
          }
          _this.eventDispatcher.queue('autocomplete.getAutocompleteResults.completed', json);
          return json;
        }
        throw new Error('getAutocompleteResults response data is malformed');
      });
    }
  }]);
  return Autocomplete;
}();
module.exports = Autocomplete;